import React from 'react';
import Input from '../Input';

interface CompanyDataSectionProps {
  formData: {
    businessName: string;
    vatNumber: string;
    fiscalCode: string;
    street: string;
    number: string;
    city: string;
    province: string;
    zipCode: string;
    phone: string;
    fax: string;
    mobile: string;
    email: string;
    website: string;
    pec: string;
    iban: string;
    mainActivity: string;
  };
  errors: Record<string, string>;
  onChange: (field: string, value: string) => void;
}

const CompanyDataSection: React.FC<CompanyDataSectionProps> = ({
  formData,
  errors,
  onChange
}) => {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm ring-1 ring-black/5 dark:ring-white/10 p-6 sm:p-8">
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
        Dati Aziendali
      </h3>

      <div className="space-y-6">
        {/* Ragione Sociale */}
        <Input
          label="Ragione Sociale *"
          type="text"
          value={formData.businessName}
          onChange={(e) => onChange('businessName', e.target.value)}
          error={errors.businessName}
          fullWidth
          required
        />

        {/* Partita IVA e Codice Fiscale */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Partita IVA * (11 cifre)"
            type="text"
            value={formData.vatNumber}
            onChange={(e) => onChange('vatNumber', e.target.value.replace(/\D/g, ''))}
            error={errors.vatNumber}
            maxLength={11}
            placeholder=""
            fullWidth
            required
          />

          <Input
            label="Codice Fiscale *"
            type="text"
            value={formData.fiscalCode}
            onChange={(e) => onChange('fiscalCode', e.target.value.toUpperCase())}
            error={errors.fiscalCode}
            maxLength={16}
            fullWidth
            required
          />
        </div>

        {/* Indirizzo */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <Input
              label="Indirizzo *"
              type="text"
              value={formData.street}
              onChange={(e) => onChange('street', e.target.value)}
              error={errors.street}
              fullWidth
              required
            />
          </div>

          <Input
            label="Numero *"
            type="text"
            value={formData.number}
            onChange={(e) => onChange('number', e.target.value)}
            error={errors.number}
            fullWidth
            required
          />
        </div>

        {/* Città, Provincia, CAP */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Input
            label="Città *"
            type="text"
            value={formData.city}
            onChange={(e) => onChange('city', e.target.value)}
            error={errors.city}
            fullWidth
            required
          />

          <Input
            label="Provincia * (es. RM)"
            type="text"
            value={formData.province}
            onChange={(e) => onChange('province', e.target.value.toUpperCase())}
            error={errors.province}
            maxLength={2}
            placeholder=""
            fullWidth
            required
          />

          <Input
            label="CAP * (5 cifre)"
            type="text"
            value={formData.zipCode}
            onChange={(e) => onChange('zipCode', e.target.value.replace(/\D/g, ''))}
            error={errors.zipCode}
            maxLength={5}
            placeholder=""
            fullWidth
            required
          />
        </div>

        {/* Contatti */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Input
            label="Telefono"
            type="tel"
            value={formData.phone}
            onChange={(e) => onChange('phone', e.target.value)}
            error={errors.phone}
            fullWidth
          />

          <Input
            label="Fax"
            type="tel"
            value={formData.fax}
            onChange={(e) => onChange('fax', e.target.value)}
            error={errors.fax}
            fullWidth
          />

          <Input
            label="Cellulare"
            type="tel"
            value={formData.mobile}
            onChange={(e) => onChange('mobile', e.target.value)}
            error={errors.mobile}
            fullWidth
          />
        </div>

        {/* Email, Website, PEC */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Input
            label="Email *"
            type="email"
            value={formData.email}
            onChange={(e) => onChange('email', e.target.value.toLowerCase())}
            error={errors.email}
            fullWidth
            required
          />

          <Input
            label="Sito Web (https://www.example.com)"
            type="url"
            value={formData.website}
            onChange={(e) => onChange('website', e.target.value)}
            error={errors.website}
            placeholder=""
            fullWidth
          />

          <Input
            label="PEC"
            type="email"
            value={formData.pec}
            onChange={(e) => onChange('pec', e.target.value.toLowerCase())}
            error={errors.pec}
            fullWidth
          />
        </div>

        {/* IBAN */}
        <Input
          label="IBAN"
          type="text"
          value={formData.iban}
          onChange={(e) => onChange('iban', e.target.value.toUpperCase().replace(/\s/g, ''))}
          error={errors.iban}
          maxLength={27}
          placeholder=""
          fullWidth
        />

        {/* Attività Principale */}
        <div className="flex flex-col">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Iscrizione per l'impresa avente come attività principale *
          </label>
          <select
            value={formData.mainActivity}
            onChange={(e) => onChange('mainActivity', e.target.value)}
            className={`w-full px-4 py-3 rounded-lg border ${
              errors.mainActivity
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500'
            } bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 transition-colors`}
            required
          >
            <option value="">Seleziona l'attività principale</option>
            <option value="Studio di Consulenza Automobilistica">Studio di Consulenza Automobilistica</option>
            <option value="Autoscuola">Autoscuola</option>
            <option value="Scuola Nautica">Scuola Nautica</option>
          </select>
          {errors.mainActivity && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.mainActivity}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyDataSection;

